import { useQuery } from "@apollo/client/react";
import { Drawer, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import {
  Upload,
  ExportDeclarationsDocument,
  ExportDeclarationsQuery,
  ExportDeclarationsQueryVariables,
  UploadForDownloadsDocument,
  UploadForDownloadsQuery,
  UploadForDownloadsQueryVariables,
} from "../../graphql/types";

import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { toast } from "react-toastify";
import { DrawerContainer } from "../../components/ui/common";
import { UploadFilters } from "../../components/UploadsDataGrid/UploadFilters";
import { UploadsDataGrid } from "../../components/UploadsDataGrid/UploadsDataGrid";
import {
  getDefaultUploadFilters,
  getUploadsDatagridQueryVariables,
  getUploadsNodes,
  IUploadsFilter,
  UploadFiltersParamNames,
} from "../../components/UploadsDataGrid/uploadsDatagridUtils";
import useAuth, { DataGridColumns } from "../../context/UserContext";
import { isDateWithinRange, setOrRemoveRangeParam, setOrRemoveSearchParam, toDayDate } from "../../lib/Util";

const initialVisibilityModelUploadsForDownloadsDatagrid = {
  [DataGridColumns.ReportDateTime]: true,
  [DataGridColumns.Created]: false,
  [DataGridColumns.Project]: true,
  [DataGridColumns.Name]: true,
  [DataGridColumns.Purpose]: false,
  [DataGridColumns.Description]: false,
  [DataGridColumns.Status]: true,
  [DataGridColumns.Actions]: true,
  [DataGridColumns.Started]: true,
  [DataGridColumns.Finished]: false,
  [DataGridColumns.UploadDuration]: false,
  [DataGridColumns.QueuedDuration]: false,
  [DataGridColumns.Destination]: true,
};

const getDefaultText = (
  allDirtyRows: boolean,
  status: string | undefined,
  hasConnectedUploadDeclarations?: boolean
) => {
  if (allDirtyRows) {
    return "Processing failed; all rows are dirty.";
  } else if (status === "failed") {
    return "Uploads will not appear as downloads failed.";
  } else if (hasConnectedUploadDeclarations) {
    return "Processing for this download will be scheduled within the hour.";
  } else if (status === "completed") {
    return "Query has completed successfully, no uploads will result.";
  } else {
    return "Uploads will appear once downloads are completed.";
  }
};

const paramNames: UploadFiltersParamNames = {
  exportNames: "drawerUploadName",
  projects: "drawerProject",
  search: "drawerSearch",
  status: "drawerStatus",
  purpose: "drawerPurpose",
  destination: "drawerDestination",
  sortBy: "drawerSortBy",
  sortDirection: "drawerSortDirection",
  reportRange: { start: "drawerStart", end: "drawerEnd" },
  uploadedRange: { start: "drawerUploaded_start", end: "drawerUploaded_end" },
};

const updateSearchParams = (params: URLSearchParams, filter: IUploadsFilter, setSearchParams: SetURLSearchParams) => {
  const {
    projects,
    exportNames,
    search,
    status,
    reportRange,
    purpose,
    uploadedRange,
    destination,
    sortBy,
    sortDirection,
  } = filter;
  const updatedParams = new URLSearchParams(params.toString());
  setOrRemoveSearchParam(paramNames.projects, projects.join(","), updatedParams);
  setOrRemoveSearchParam(paramNames.exportNames, exportNames.join(","), updatedParams);
  setOrRemoveSearchParam(paramNames.search, search, updatedParams);
  setOrRemoveSearchParam(paramNames.status, status, updatedParams);
  setOrRemoveSearchParam(paramNames.purpose, purpose, updatedParams);
  setOrRemoveSearchParam(paramNames.destination, destination, updatedParams);
  setOrRemoveSearchParam(paramNames.sortBy, sortBy, updatedParams);
  setOrRemoveSearchParam(paramNames.sortDirection, sortDirection, updatedParams);
  setOrRemoveRangeParam(paramNames.reportRange.start, paramNames.reportRange.end, updatedParams, reportRange);
  setOrRemoveRangeParam(paramNames.uploadedRange.start, paramNames.reportRange.end, updatedParams, uploadedRange);
  if (updatedParams.toString() !== params.toString()) setSearchParams(updatedParams);
};

const resetParams = (setSearchParams: SetURLSearchParams) => {
  setSearchParams((prevState) => {
    const updatedParams = new URLSearchParams(prevState.toString());
    updatedParams.delete(paramNames.projects);
    updatedParams.delete(paramNames.exportNames);
    updatedParams.delete(paramNames.search);
    updatedParams.delete(paramNames.status);
    updatedParams.delete(paramNames.purpose);
    updatedParams.delete(paramNames.destination);
    updatedParams.delete(paramNames.sortBy);
    updatedParams.delete(paramNames.sortDirection);
    updatedParams.delete(paramNames.reportRange.start);
    updatedParams.delete(paramNames.reportRange.end);
    updatedParams.delete(paramNames.reportRange.start);
    updatedParams.delete(paramNames.reportRange.end);
    return updatedParams;
  });
};

const filterUploads = (uploads: Upload[] | undefined, filter: IUploadsFilter): Upload[] => {
  if (!uploads) return [];
  return uploads.filter((upload) => {
    if (filter.destination && upload.destination !== filter.destination) return false;
    if (filter.exportNames.length > 0 && !filter.exportNames.includes(upload.name)) return false;
    if (filter.projects.length > 0 && !filter.projects.includes(upload.project)) return false;
    if (filter.purpose && upload.purpose !== filter.purpose) return false;
    if (filter.search && !upload.name.includes(filter.search)) return false;
    if (filter.status && upload.status !== filter.status) return false;

    if (
      filter.uploadedRange.start &&
      filter.uploadedRange.end &&
      (!upload.started ||
        !isDateWithinRange(new Date(upload.started), filter.uploadedRange.start, filter.uploadedRange.end))
    ) {
      return false;
    }
    if (
      filter.reportRange.start &&
      filter.reportRange.end &&
      upload.report_datetime &&
      !isDateWithinRange(toDayDate(upload.report_datetime.iso), filter.reportRange.start, filter.reportRange.end)
    ) {
      return false;
    }
    return true;
  });
};

export type UploadsForDownloadDrawerProps = {
  handleCloseDrawer: () => void;
  hasConnectedUploadDeclarations: boolean;
  areAllRowsDirty: boolean;
  downloadStatus: string;
  downloadPartIds: string[];
  downloadName: string;
};

const UploadsForDownloadDrawer = ({
  handleCloseDrawer,
  hasConnectedUploadDeclarations,
  areAllRowsDirty,
  downloadStatus,
  downloadPartIds,
  downloadName,
}: UploadsForDownloadDrawerProps) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const treeDataQuery = useQuery<ExportDeclarationsQuery, ExportDeclarationsQueryVariables>(ExportDeclarationsDocument);

  const theme = useTheme();

  const [filter, setFilter] = useState<IUploadsFilter>(
    getDefaultUploadFilters(searchParams, false, undefined, paramNames)
  );

  const { data, loading, error } = useQuery<UploadForDownloadsQuery, UploadForDownloadsQueryVariables>(
    UploadForDownloadsDocument,
    {
      fetchPolicy: "no-cache",
      variables: {
        downloadPartIds: downloadPartIds,
      },
    }
  );

  useEffect(() => {
    updateSearchParams(searchParams, filter, setSearchParams);
  }, [filter, searchParams, setSearchParams]);

  if (error) {
    toast.error("There was a problem processing the request. Contact support if the problem persist");
    handleCloseDrawer();
  }

  if (areAllRowsDirty === undefined && !downloadStatus) {
    toast.error("Page was accessed with the wrong URL params.");
    console.error("Uploads for downloads drawer was accessed with the wrong URL params.");
    handleCloseDrawer();
    return null;
  }

  const uploads = data?.uploads?.edges.map((edge) => edge?.node as Upload);

  const filteredUploads = filterUploads(uploads, filter);

  const { selectedNodes, nonSelectedNodes } = getUploadsNodes(treeDataQuery.data, filter);

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={(event: ChangeEvent) => {
        event.stopPropagation();
        resetParams(setSearchParams);
        handleCloseDrawer();
      }}
      data-testid="uploads-for-download-drawer"
    >
      <DrawerContainer>
        <Stack spacing="20px" sx={{ width: "100%" }}>
          <IconButton onClick={handleCloseDrawer} sx={{ alignSelf: "flex-end" }}>
            <XIcon width="24px" height="24px" color={theme.palette.text50.main} />
          </IconButton>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Stack direction="row">
              <Typography variant="heading.five" sx={{ fontWeight: "bold" }}>
                Uploads for download {downloadName}
              </Typography>
            </Stack>
            <UploadFilters
              loading={treeDataQuery.loading}
              filter={filter}
              setFilter={setFilter}
              queryVariables={getUploadsDatagridQueryVariables(filter)}
              selectedNodesInFilter={selectedNodes}
              nonSelectedNodesInFilter={nonSelectedNodes}
              datagridPreferencesElement={user.datagridPreferences.uploadsForDownload}
              preferenceElementToUpdate="uploadsForDownload"
              initialVisibilityModel={initialVisibilityModelUploadsForDownloadsDatagrid}
              hideTextSearch
            />
          </Stack>
          <UploadsDataGrid
            datagridPreferencesElement={user.datagridPreferences.uploadsForDownload}
            filter={filter}
            loading={loading}
            onRowsScrollEnd={() => {}}
            defaultText={getDefaultText(!!areAllRowsDirty, downloadStatus, !!hasConnectedUploadDeclarations)}
            rows={filteredUploads}
            initialVisibilityModel={initialVisibilityModelUploadsForDownloadsDatagrid}
            preferenceElementToUpdate="uploadsForDownload"
          />
        </Stack>
      </DrawerContainer>
    </Drawer>
  );
};
export { UploadsForDownloadDrawer };
